import React, { useState, useMemo } from 'react'
import { router, Head } from '@inertiajs/react'
import OnboardingShell from '@/components/onboarding/OnboardingShell'
import { HelpTooltip } from '@/components/HelpTooltip'
import RoomTypeModal, { type LinkState, type RoomDraft } from '@/components/pricing-rules/RoomTypeModal'
import DerivedRatePlansModal from '@/components/pricing-rules/DerivedRatePlansModal'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'
import { currencySymbol } from '@/lib/calendar-utils'
import { formatMoneySym } from '@/lib/format'
import { prefillSuggestedRates } from '@/lib/suggestions'
import { useBrand } from '@/hooks/useBrand'
import {
  buildRatesState,
  buildBoundsState,
  buildLeadPlansState,
  buildLeadPlansPayload,
  roomHasRate,
  countRoomsWithRates,
  stagedRateEntries,
  type DerivedPlanInput,
  type LeadPlanState,
} from '@/lib/onboarding'
import {
  BedDouble,
  Users,
  Layers,
  Loader2,
  Sparkles,
  ShieldCheck,
  AlertCircle,
  PencilLine,
  GitBranch,
  Undo2,
  ClipboardList,
} from 'lucide-react'
import type { SharedProps, RoomsSetupRoomType, BaseRateSuggestions } from '@/types/shared'

interface ReviewStructureProps extends SharedProps {
  roomTypes: RoomsSetupRoomType[]
  baseRates: Record<string, Record<string, number>>
  baseRateSuggestions: BaseRateSuggestions
}

/**
 * Step 3 of the onboarding journey, in two halves:
 *   1. "What we found" — a read-only inventory of the rooms, plans and rates
 *      pulled from Eviivo, so the operator can sanity-check the connection.
 *   2. "Quick questions" — the two core questions per room (lead rate plan,
 *      starting price). ALL optional: every question has a visible skip, and
 *      Finish is never blocked. Answers go through the same
 *      UpdateRoomsSetupMutation contract as the Pricing Rules Rooms Setup page.
 */
export default function ReviewStructure({
  currentUser,
  property,
  currentAccount,
  roomTypes,
  baseRates,
  baseRateSuggestions,
}: ReviewStructureProps) {
  const sym = currencySymbol(property.currency)
  const brand = useBrand()

  // What Eviivo gave us — frozen for the read-only inventory section.
  const seededRates = useMemo(() => buildRatesState(roomTypes, baseRates), [roomTypes, baseRates])

  // The operator's editable answers, seeded from the same starting point.
  const [rates, setRates] = useState(() => buildRatesState(roomTypes, baseRates))
  const [bounds, setBounds] = useState(() => buildBoundsState(roomTypes))
  const [leadPlans, setLeadPlans] = useState<Record<string, LeadPlanState>>(() =>
    buildLeadPlansState(roomTypes)
  )
  const [skipped, setSkipped] = useState<Record<string, boolean>>({})
  const [submitting, setSubmitting] = useState(false)
  const [editingRoomId, setEditingRoomId] = useState<number | null>(null)
  const [derivedRatePlansRoomId, setDerivedRatePlansRoomId] = useState<number | null>(null)

  // A suggestion chip opens the same price editor as "Add a price", with the
  // history-derived amount pre-typed into the empty base-rate field. Prefill
  // lives only in the editor's draft — Cancel discards it, Apply stages it like
  // any hand-typed answer. Skipping stays untouched.
  const [suggestionPrefillRoomId, setSuggestionPrefillRoomId] = useState<number | null>(null)

  function openEditorWithSuggestion(roomId: number) {
    setSuggestionPrefillRoomId(roomId)
    setEditingRoomId(roomId)
  }

  function editorRatesFor(rt: RoomsSetupRoomType): Record<string, string> {
    const current = rates[rt.eviivoRoomTypeId] || {}
    const suggested = baseRateSuggestions[String(rt.id)]?.suggestedAmount
    if (suggestionPrefillRoomId !== rt.id || suggested == null) return current
    return prefillSuggestedRates(current, suggested)
  }

  const editingRoom = editingRoomId ? roomTypes.find((rt) => rt.id === editingRoomId) : null
  // Onboarding stages base rates only — room linking is configured later on the
  // Pricing Rules page, so an empty parent list keeps the link toggle off.
  const noLink: LinkState = { parentRoomTypeId: null, offsetType: 'percentage', offsetValue: '' }

  const derivedRatePlansRoom = derivedRatePlansRoomId
    ? roomTypes.find((rt) => rt.id === derivedRatePlansRoomId)
    : null
  const derivedRatePlansLocal = derivedRatePlansRoomId
    ? leadPlans[String(derivedRatePlansRoomId)]
    : null

  const modalRoom = useMemo(() => {
    if (!derivedRatePlansRoom || !derivedRatePlansLocal) return null
    const effectiveLeadId = derivedRatePlansLocal.leadRatePlanId
    if (effectiveLeadId == null) return null
    return {
      roomTypeId: derivedRatePlansRoom.id,
      leadPlanId: effectiveLeadId,
      ratePlans: derivedRatePlansRoom.ratePlans,
      initialDerivedPlans: derivedRatePlansLocal.derivedPlans,
    }
  }, [derivedRatePlansRoom, derivedRatePlansLocal])

  const totalPlans = useMemo(
    () => roomTypes.reduce((sum, rt) => sum + (rt.ratePlans?.length || 0), 0),
    [roomTypes]
  )
  const seededCount = useMemo(
    () => countRoomsWithRates(roomTypes, seededRates),
    [roomTypes, seededRates]
  )
  const answeredCount = countRoomsWithRates(roomTypes, rates)

  // Early "not priced yet" signal, derived from STAGED lead choices (nothing is
  // persisted until Finish). A plan is unpriced once its room has a chosen lead but
  // the plan itself has no staged offset — the pipeline would price neither. Surfaced
  // here because the app-wide WarningBanner is intentionally hidden during onboarding.
  const unpricedRooms = useMemo(() => {
    return roomTypes.filter((rt) => {
      const leadId = leadPlans[String(rt.id)]?.leadRatePlanId ?? null
      if (leadId == null) return false
      const derived = leadPlans[String(rt.id)]?.derivedPlans ?? {}
      return (rt.ratePlans || []).some((rp) => rp.id !== leadId && !(String(rp.id) in derived))
    })
  }, [roomTypes, leadPlans])
  const unpricedPlanTotal = useMemo(
    () =>
      unpricedRooms.reduce((sum, rt) => {
        const leadId = leadPlans[String(rt.id)]?.leadRatePlanId ?? null
        const derived = leadPlans[String(rt.id)]?.derivedPlans ?? {}
        return sum + (rt.ratePlans || []).filter((rp) => rp.id !== leadId && !(String(rp.id) in derived)).length
      }, 0),
    [unpricedRooms, leadPlans]
  )

  function handleApplyRoom(roomId: number, draft: RoomDraft) {
    const rt = roomTypes.find((r) => r.id === roomId)
    if (!rt) return
    setRates((prev) => ({
      ...prev,
      [rt.eviivoRoomTypeId]: { ...prev[rt.eviivoRoomTypeId], ...draft.rates },
    }))
    setBounds((prev) => ({ ...prev, [String(roomId)]: { ...draft.bounds } }))
    setSkipped((prev) => ({ ...prev, [String(roomId)]: false }))
  }

  function handleLeadPlanChange(roomTypeId: number, planId: number) {
    setLeadPlans((prev) => ({
      ...prev,
      [String(roomTypeId)]: {
        leadRatePlanId: planId,
        derivedPlans: prev[String(roomTypeId)]?.derivedPlans || {},
      },
    }))
    setSkipped((prev) => ({ ...prev, [String(roomTypeId)]: false }))
  }

  function handleDerivedPlansChange(
    roomTypeId: number,
    derivedPayload: Record<string, DerivedPlanInput>
  ) {
    setLeadPlans((prev) => ({
      ...prev,
      [String(roomTypeId)]: {
        leadRatePlanId: prev[String(roomTypeId)]?.leadRatePlanId ?? null,
        derivedPlans: derivedPayload,
      },
    }))
    setDerivedRatePlansRoomId(null)
  }

  function handleConfirm() {
    setSubmitting(true)
    router.post(
      `${property.basePath}/onboarding/confirm`,
      {
        base_rates: rates,
        bounds,
        lead_plans: buildLeadPlansPayload(leadPlans),
      } as any,
      { onFinish: () => setSubmitting(false) }
    )
  }

  return (
    <OnboardingShell
      currentStep={3}
      contextChip={{ label: 'Setting up', value: property.name, kind: 'property' }}
      signedInAs={currentUser.email}
      portfolioPath={currentAccount?.basePath ?? null}
      exitHref={`${property.basePath}/calendar/prices`}
      wide
    >
      <Head title="Review structure" />
      <div className="space-y-6">
        {/* Page header */}
        <div className="animate-slide-in" style={{ animationDelay: '0ms' }}>
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-bold tracking-tight text-foreground">
              Here&apos;s what we found
            </h1>
            <HelpTooltip content={`${brand} just read this property's rooms, rate plans and prices from your PMS. Check it looks right, answer the questions below, then finish.`} />
          </div>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Pulled from your PMS for {property.name}. Check it over, answer the questions below,
            then finish setup.
          </p>
        </div>

        {/* Summary strip */}
        <div className="animate-slide-in flex flex-wrap gap-2" style={{ animationDelay: '50ms' }}>
          <span className="inline-flex items-center gap-1.5 rounded-lg border border-border/60 bg-white px-3 py-1.5 text-xs font-medium text-muted-foreground shadow-sm">
            <BedDouble className="h-3.5 w-3.5 text-primary" />
            <span className="tabular-nums font-semibold text-foreground">{roomTypes.length}</span>
            {roomTypes.length === 1 ? 'room type' : 'room types'}
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-lg border border-border/60 bg-white px-3 py-1.5 text-xs font-medium text-muted-foreground shadow-sm">
            <Layers className="h-3.5 w-3.5 text-primary" />
            <span className="tabular-nums font-semibold text-foreground">{totalPlans}</span>
            {totalPlans === 1 ? 'rate plan' : 'rate plans'}
          </span>
          <span
            className={cn(
              'inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-medium shadow-sm',
              seededCount === roomTypes.length
                ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                : 'border-amber-200 bg-amber-50 text-amber-700'
            )}
          >
            {seededCount === roomTypes.length ? (
              <ShieldCheck className="h-3.5 w-3.5" />
            ) : (
              <AlertCircle className="h-3.5 w-3.5" />
            )}
            Prices found for{' '}
            <span className="tabular-nums font-semibold">
              {seededCount} of {roomTypes.length}
            </span>
            rooms
          </span>
        </div>

        {/* Unpriced-plans notice — the "early" alert during onboarding (the app-wide
            banner is hidden in the wizard). Calm and non-blocking: the operator can
            finish now and set offsets later. Never one row per plan — consolidated. */}
        {unpricedPlanTotal > 0 && (
          <div
            className="animate-slide-in rounded-xl border border-amber-200 bg-amber-50 p-4 shadow-sm"
            style={{ animationDelay: '75ms' }}
          >
            <div className="flex items-start gap-3">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
              <div className="min-w-0 text-sm text-amber-800">
                <p className="font-semibold">
                  {unpricedPlanTotal} {unpricedPlanTotal === 1 ? 'rate plan' : 'rate plans'} on{' '}
                  {unpricedRooms.length} {unpricedRooms.length === 1 ? 'room' : 'rooms'} not priced yet
                </p>
                <p className="mt-1 leading-relaxed text-amber-800/90">
                  {unpricedRooms.map((rt) => rt.name).join(', ')} —{' '}
                  {unpricedPlanTotal === 1 ? 'this plan has' : 'these plans have'} no offset from the
                  lead rate, so {brand} won&apos;t price {unpricedPlanTotal === 1 ? 'it' : 'them'}. Open each
                  room below and choose how {unpricedPlanTotal === 1 ? 'it follows' : 'they follow'} the lead —
                  a discount, a premium, or the same price. You can finish now and set these later from Rooms Setup.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* ── Section 1: What we found (read-only inventory) ── */}
        <div className="animate-slide-in" style={{ animationDelay: '100ms' }}>
          <div className="overflow-hidden rounded-xl border border-border/60 bg-white shadow-sm">
            <div className="border-b border-border/40 px-5 py-4">
              <div className="flex items-center gap-2">
                <h2 className="text-sm font-semibold text-foreground">Your rooms, as your PMS has them</h2>
                <HelpTooltip content="A read-only snapshot of the structure we imported: each room type, how many units it has, how many guests it sleeps, its rate plans, and any current price we could read from your PMS. If something looks wrong here, fix it in your PMS and re-sync later from the Dashboard." />
              </div>
              <p className="text-xs text-muted-foreground">
                Read-only — this is exactly what came across from your PMS.
              </p>
            </div>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Room</TableHead>
                    <TableHead className="text-center">
                      <span className="inline-flex items-center gap-1">
                        <Layers className="h-3 w-3" />
                        Units
                      </span>
                    </TableHead>
                    <TableHead className="text-center">
                      <span className="inline-flex items-center gap-1">
                        <Users className="h-3 w-3" />
                        Sleeps
                      </span>
                    </TableHead>
                    <TableHead>Rate plans</TableHead>
                    <TableHead className="text-right">Price in your PMS</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {roomTypes.map((rt) => {
                    const isPerRoom = rt.priceStrategy === 'per_room'
                    const seeded = stagedRateEntries(rt, seededRates)
                    return (
                      <TableRow key={rt.id}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <span className="text-[13px] font-semibold text-foreground">{rt.name}</span>
                            <span
                              className={cn(
                                'inline-flex items-center whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-medium tracking-wide',
                                isPerRoom
                                  ? 'bg-slate-100 text-slate-700 ring-1 ring-inset ring-slate-500/20'
                                  : 'bg-primary/10 text-primary ring-1 ring-inset ring-primary/20'
                              )}
                            >
                              {isPerRoom ? 'Priced per room' : 'Priced per guest'}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="text-center text-sm tabular-nums text-foreground">
                          {rt.totalUnits}
                        </TableCell>
                        <TableCell className="text-center text-sm tabular-nums text-foreground">
                          {rt.maxOccupancy}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {rt.ratePlans.map((rp) => (
                              <span
                                key={rp.id}
                                className={cn(
                                  'inline-flex items-center rounded-md px-2 py-0.5 text-[10px] font-medium',
                                  rp.isLead
                                    ? 'bg-primary/10 text-primary'
                                    : 'bg-muted text-muted-foreground'
                                )}
                              >
                                {rp.name}
                              </span>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          {seeded.length > 0 ? (
                            <span className="inline-flex flex-wrap justify-end gap-1.5">
                              {seeded.map((entry) => (
                                <span
                                  key={entry.occupancy}
                                  className="text-sm tabular-nums font-semibold text-foreground"
                                >
                                  {!isPerRoom && (
                                    <span className="mr-0.5 text-[10px] font-medium text-muted-foreground">
                                      {entry.occupancy}
                                      {entry.occupancy === 1 ? ' guest' : ' guests'}
                                    </span>
                                  )}
                                  {sym}
                                  {entry.value}
                                </span>
                              ))}
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700 ring-1 ring-inset ring-amber-500/20">
                              <AlertCircle className="h-3 w-3" />
                              No price found in your PMS
                            </span>
                          )}
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </div>
          </div>
        </div>

        {/* ── Section 2: The core questions (all skippable) ── */}
        <div className="animate-slide-in" style={{ animationDelay: '150ms' }}>
          <div className="mb-3">
            <div className="flex items-center gap-2">
              <h2 className="text-sm font-semibold text-foreground">
                Two quick questions per room
              </h2>
              <HelpTooltip content="The lead rate plan is the plan we optimise; your other plans follow it. The starting price is the everyday price we build daily prices around." />
            </div>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Pick each room&apos;s lead plan and everyday price. Everything here can be changed
              later in Pricing Rules.
            </p>
          </div>

          <div className="overflow-hidden rounded-xl border border-border/60 bg-white shadow-sm">
            {roomTypes.map((rt, index) => {
              const isSkipped = !!skipped[String(rt.id)]
              const hasAnswer = roomHasRate(rt, rates)
              const seededHere = roomHasRate(rt, seededRates)
              const entries = stagedRateEntries(rt, rates)
              const leadState = leadPlans[String(rt.id)]
              const effectiveLeadId = leadState?.leadRatePlanId ?? null
              const derivedCount = Object.keys(leadState?.derivedPlans || {}).length
              const isPerRoom = rt.priceStrategy === 'per_room'
              // History-derived price hint — only where Eviivo gave us nothing
              // and the operator hasn't answered yet. Never a nag, always a door.
              const suggestedAmount =
                !seededHere && !hasAnswer
                  ? baseRateSuggestions[String(rt.id)]?.suggestedAmount ?? null
                  : null

              return (
                <div
                  key={rt.id}
                  className={cn('px-5 py-4', index > 0 && 'border-t border-border/40')}
                >
                  {/* Room identity row */}
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-3">
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/8">
                        <BedDouble className="h-4 w-4 text-primary" />
                      </div>
                      <div>
                        <span className="text-sm font-semibold text-foreground">{rt.name}</span>
                        <p className="text-xs text-muted-foreground">
                          {rt.totalUnits} {rt.totalUnits === 1 ? 'unit' : 'units'} · sleeps{' '}
                          {rt.maxOccupancy}
                        </p>
                      </div>
                      {!seededHere && !isSkipped && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700 ring-1 ring-inset ring-amber-500/20">
                          <AlertCircle className="h-3 w-3" />
                          No price found in your PMS
                        </span>
                      )}
                    </div>

                    {isSkipped ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 rounded-lg text-xs text-muted-foreground"
                        onClick={() => setSkipped((prev) => ({ ...prev, [String(rt.id)]: false }))}
                      >
                        <Undo2 className="mr-1.5 h-3.5 w-3.5" />
                        Undo skip
                      </Button>
                    ) : (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 rounded-lg text-xs text-muted-foreground hover:text-foreground"
                        onClick={() => setSkipped((prev) => ({ ...prev, [String(rt.id)]: true }))}
                      >
                        Skip
                      </Button>
                    )}
                  </div>

                  {/* The two questions (hidden while skipped) */}
                  {isSkipped ? (
                    <p className="mt-3 rounded-lg bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
                      Skipped.
                    </p>
                  ) : (
                    <div className="mt-4 grid gap-4 sm:grid-cols-2">
                      {/* Question 1: lead rate plan */}
                      <div className="space-y-1.5">
                        <Label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                          Which plan should we optimise?
                        </Label>
                        {rt.ratePlans.length > 0 ? (
                          <>
                            <div className="flex items-center gap-2">
                              <Select
                                value={effectiveLeadId != null ? String(effectiveLeadId) : undefined}
                                onValueChange={(val) => handleLeadPlanChange(rt.id, parseInt(val, 10))}
                              >
                                <SelectTrigger className="h-9 flex-1 text-sm">
                                  <SelectValue placeholder="Choose a plan" />
                                </SelectTrigger>
                                <SelectContent>
                                  {rt.ratePlans.map((rp) => (
                                    <SelectItem key={rp.id} value={String(rp.id)}>
                                      {rp.name}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              {effectiveLeadId != null && rt.ratePlans.length > 1 && (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  className="h-9 shrink-0 gap-1.5 rounded-lg text-xs text-primary hover:text-primary/80"
                                  onClick={() => setDerivedRatePlansRoomId(rt.id)}
                                >
                                  <GitBranch className="h-3 w-3" />
                                  Other plans
                                  {derivedCount > 0 && (
                                    <span className="rounded-full bg-primary/10 px-1.5 text-[10px] font-semibold text-primary tabular-nums">
                                      {derivedCount}
                                    </span>
                                  )}
                                </Button>
                              )}
                            </div>
                            <p className="text-[10px] text-muted-foreground">
                              The plan daily prices are calculated for — other plans follow it.
                            </p>
                          </>
                        ) : (
                          <p className="rounded-lg bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
                            No rate plans came across for this room.
                          </p>
                        )}
                      </div>

                      {/* Question 2: starting price */}
                      <div className="space-y-1.5">
                        <Label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                          What&apos;s the everyday price?
                        </Label>
                        {hasAnswer ? (
                          <div className="flex items-center justify-between gap-2 rounded-lg border border-border/60 bg-muted/20 px-3 py-1.5">
                            <span className="flex flex-wrap items-center gap-x-2.5 gap-y-0.5">
                              {entries.map((entry) => (
                                <span key={entry.occupancy} className="text-sm tabular-nums font-semibold text-foreground">
                                  {!isPerRoom && (
                                    <span className="mr-0.5 text-[10px] font-medium text-muted-foreground">
                                      {entry.occupancy}
                                      {entry.occupancy === 1 ? ' guest' : ' guests'}
                                    </span>
                                  )}
                                  {sym}
                                  {entry.value}
                                </span>
                              ))}
                            </span>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="h-7 shrink-0 gap-1 rounded-lg px-2 text-xs text-muted-foreground hover:text-foreground"
                              onClick={() => setEditingRoomId(rt.id)}
                            >
                              <PencilLine className="h-3 w-3" />
                              Adjust
                            </Button>
                          </div>
                        ) : (
                          <div className="flex items-center justify-between gap-2 rounded-lg border border-dashed border-amber-300 bg-amber-50/50 px-3 py-1.5">
                            <span className="text-xs text-amber-800">
                              Add a typical nightly price so previews mean something
                            </span>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="h-7 shrink-0 gap-1 rounded-lg px-2 text-xs font-semibold text-amber-700 hover:bg-amber-100 hover:text-amber-800"
                              onClick={() => setEditingRoomId(rt.id)}
                            >
                              <PencilLine className="h-3 w-3" />
                              Add a price
                            </Button>
                          </div>
                        )}
                        {suggestedAmount != null && (
                          <button
                            type="button"
                            onClick={() => openEditorWithSuggestion(rt.id)}
                            title="From this room's own bookings over the last 12 months. Opens the price editor with this amount filled in — you review before anything counts."
                            className="inline-flex items-center gap-1.5 rounded-full border border-primary/25 bg-primary/5 px-2.5 py-1 text-[11px] font-medium text-primary transition-colors hover:border-primary/40 hover:bg-primary/10"
                          >
                            <Sparkles className="h-3 w-3" />
                            <span>
                              Your booking history suggests{' '}
                              <span className="font-semibold tabular-nums">
                                {formatMoneySym(suggestedAmount, sym)}
                              </span>
                              /night
                            </span>
                          </button>
                        )}
                        <p className="text-[10px] text-muted-foreground">
                          A typical mid-week price — the anchor daily prices move around.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>

        {/* Finish bar */}
        <div
          className="animate-slide-in flex flex-col gap-3 pt-2 sm:flex-row sm:items-center sm:justify-between"
          style={{ animationDelay: '200ms' }}
        >
          <p className="text-xs text-muted-foreground">
            {answeredCount > 0 ? (
              <>
                Prices set for{' '}
                <span className="tabular-nums font-semibold text-foreground">
                  {answeredCount} of {roomTypes.length}
                </span>{' '}
                rooms.
              </>
            ) : (
              <>No prices set yet.</>
            )}
          </p>
          <Button
            onClick={handleConfirm}
            disabled={submitting}
            className="rounded-lg px-6"
            size="lg"
          >
            {submitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Sparkles className="mr-2 h-4 w-4" />
                Finish setup
              </>
            )}
          </Button>
        </div>

        {/* Expectation note */}
        <div className="animate-slide-in" style={{ animationDelay: '250ms' }}>
          <div className="flex items-start gap-2 rounded-lg border border-primary/20 bg-primary/5 px-4 py-3 text-sm text-foreground/80">
            <ClipboardList className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
            <p>
              When you finish, {brand} starts preparing preview prices in the background.{' '}
              <span className="font-medium text-foreground">
                Nothing is sent to your PMS until you press Go&nbsp;Live
              </span>{' '}
              — previews are yours to watch and tune first.
            </p>
          </div>
        </div>
      </div>

      {/* Room price editor — the same editor the Pricing Rules page uses */}
      {editingRoom && (
        <RoomTypeModal
          key={editingRoom.id}
          open={editingRoomId != null}
          onOpenChange={(open) => {
            if (!open) {
              setEditingRoomId(null)
              setSuggestionPrefillRoomId(null)
            }
          }}
          room={editingRoom}
          currencySymbol={sym}
          rates={editorRatesFor(editingRoom)}
          bounds={bounds[String(editingRoom.id)] || { min: '', max: '' }}
          link={noLink}
          leadRatePlanId={
            leadPlans[String(editingRoom.id)]?.leadRatePlanId ?? editingRoom.leadRatePlanId
          }
          parentOptions={[]}
          derivedPlanCount={
            Object.keys(leadPlans[String(editingRoom.id)]?.derivedPlans || {}).length
          }
          onApply={handleApplyRoom}
          onLeadPlanChange={handleLeadPlanChange}
          onOpenDerivedRates={(roomTypeId) => setDerivedRatePlansRoomId(roomTypeId)}
        />
      )}

      {/* Derived rate plans modal */}
      {modalRoom && (
        <DerivedRatePlansModal
          open={derivedRatePlansRoomId != null}
          onOpenChange={(open) => {
            if (!open) setDerivedRatePlansRoomId(null)
          }}
          roomTypeId={modalRoom.roomTypeId}
          leadPlanId={modalRoom.leadPlanId}
          ratePlans={modalRoom.ratePlans}
          initialDerivedPlans={modalRoom.initialDerivedPlans}
          currencySymbol={sym}
          onSave={handleDerivedPlansChange}
        />
      )}
    </OnboardingShell>
  )
}
